import {BASE_AUDIT_API, fetchAPI, getHttpErrorMessage} from '@/services/api';
import {buildSeataTxHeaders} from '@/services/seataTxHolder';
import {buildSeedPayload} from '@/pages/components/audit-program/templates/defaults';

export interface AuditProgramPageTemplateVO {
    id?: number;
    sectionCode: string;
    pageCode: string;
    version?: number;
    status?: string;
    content?: Record<string, unknown>;
}

export interface AuditProgramPageTemplateSavePayload {
    sectionCode: string;
    pageCode: string;
    content: Record<string, unknown>;
    status?: string;
}

const base = `${BASE_AUDIT_API}/audit-program/template`;

export async function listProgramTemplates(sectionCode?: string): Promise<AuditProgramPageTemplateVO[]> {
    const qs = new URLSearchParams();
    if (sectionCode) qs.set('sectionCode', sectionCode);
    const suffix = qs.toString() ? `?${qs}` : '';
    return fetchAPI<AuditProgramPageTemplateVO[]>(`${base}/list${suffix}`);
}

export async function getProgramTemplate(
    sectionCode: string,
    pageCode: string,
): Promise<AuditProgramPageTemplateVO | null> {
    const qs = new URLSearchParams({sectionCode, pageCode});
    return fetchAPI<AuditProgramPageTemplateVO | null>(`${base}/get?${qs}`);
}

/** Published pageCode -> content for one section. */
export async function getProgramSectionTemplates(
    sectionCode: string,
): Promise<Record<string, Record<string, unknown>>> {
    const qs = new URLSearchParams({sectionCode});
    return fetchAPI<Record<string, Record<string, unknown>>>(`${base}/section?${qs}`);
}

export async function saveProgramTemplate(payload: AuditProgramPageTemplateSavePayload): Promise<boolean> {
    return fetchAPI<boolean>(`${base}/save`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function seedProgramTemplates(overwrite = false): Promise<number> {
    return fetchAPI<number>(`${base}/seed`, {
        method: 'POST',
        body: JSON.stringify(buildSeedPayload(overwrite)),
    });
}

export interface ProgramTemplateImportResult {
    imported: number;
    skipped: number;
    pages: Array<{sectionCode: string; pageCode: string}>;
    skippedPages: string[];
}

function getBaseUrl() {
    return (import.meta.env.VITE_APP_BASE_API as string | undefined) || '/api';
}

/** Upload APM .xlsx/.xlsm and overwrite published program page templates. */
export async function importProgramTemplatesFromExcel(file: File): Promise<ProgramTemplateImportResult> {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('adminToken') || localStorage.getItem('Admin-Token');
    const url = `${getBaseUrl()}${base}/import-excel`;
    const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
            ...(token ? {'Admin-Token': token} : {}),
            ...(token ? {Authorization: `Bearer ${token}`} : {}),
            ...buildSeataTxHeaders(),
        },
        body: formData,
    });
    if (!res.ok) {
        throw new Error(await getHttpErrorMessage(res, 'Excel import failed'));
    }
    const result = await res.json();
    if (result.code !== 0) {
        throw new Error(result.msg || 'Excel import failed');
    }
    return result.data as ProgramTemplateImportResult;
}
